<template>
  <section class="latestnews">
    <div class="container">
      <div class="latestnews__controls" ref="controls">
        <div
          class="latestnews__control latestnews__control--prev"
          @click="slideNext"
        >
          <Icon type="arrow-left" />
        </div>
        <div
          class="latestnews__control latestnews__control--next"
          @click="slidePrev"
        >
          <Icon type="arrow-right" />
        </div>
      </div>
    </div>
    <div class="latestnews_container" ref="slider">
      <a
        v-for="item in newsitems"
        :key="`newsitem-${item.url}`"
        class="newsitem_container"
        :href="item.url"
        @mouseover="hoverOverItem($event)"
        @mouseleave="hoverOffItem($event)"
        :target="item.external ? '_blank' : null"
      >
        <div class="newsitem_image__container">
          <div
            class="newsitem_image"
            :style="`background-image: url('${
              item.image ? item.image : ''
            }'); `"
          />
        </div>
        <span class="newsitem_date" v-if="item.date">{{ item.date }}</span>

        <div class="newsitem_title__container">
          <span class="newsitem_title">
            {{ item.title }}
            <span
              v-if="item.type === 'retrospect'"
              class="newsitem_title__label"
            >
              — terugblik
            </span>
          </span>
          <div class="newsitem_title__arrow_container">
            <Icon type="arrow-right" class="newsitem_title__arrow" />
          </div>
        </div>
      </a>
    </div>
  </section>
</template>

<script>
import { defineComponent, onMounted, ref, inject } from "vue";
import gsap from "gsap";
import { Draggable } from "gsap/Draggable";
import { InertiaPlugin } from "gsap/InertiaPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(Draggable, InertiaPlugin, ScrollTrigger);

export default defineComponent({
  props: {
    newsitems: { type: Array, required: true },
  },
  setup(props) {
    const emitter = inject("emitter");

    const slider = ref(null);
    const slideWidth = ref(null);
    const slideAnimation = ref(null);
    const proxyElement = ref(null);
    const updateProgress = ref(null);
    const controls = ref(null);

    onMounted(() => {
      const latestnewsContainer = slider.value;
      const newsitems = latestnewsContainer.querySelectorAll(
        ".newsitem_container"
      );
      const newsitemsImages =
        latestnewsContainer.querySelectorAll(".newsitem_image");
      const newsitemsTitle = latestnewsContainer.querySelectorAll(
        ".newsitem_title__container"
      );

      const newsitemsLength = newsitems?.length;
      slideWidth.value = newsitems[0].offsetWidth;
      const wrapWidth = slideWidth.value * newsitemsLength;

      gsap.set(newsitems, {
        xPercent: function (i) {
          return i * 100;
        },
      });
      const [prevControl, nextControl] = controls.value.children;

      gsap.set([prevControl, nextControl], { autoAlpha: 0 });
      gsap.set(controls.value, { css: { display: "block" } });

      var wrapX = gsap.utils.wrap(-100, (newsitemsLength - 1) * 100);

      slideAnimation.value = gsap
        .timeline({ paused: true, repeat: -1 })
        .to(newsitems, {
          xPercent: "+=" + newsitemsLength * 100,
          duration: 10,
          ease: "none",
          modifiers: {
            xPercent: wrapX,
          },
        });

      proxyElement.value = document.createElement("div");

      const progressWrap = gsap.utils.wrap(0, 1);
      updateProgress.value = () => {
        slideAnimation.value.progress(
          progressWrap(gsap.getProperty(proxyElement.value, "x") / wrapWidth)
        );
      };

      var draggable = new Draggable(proxyElement.value, {
        trigger: slider.value,
        inertia: true,
        type: "x",
        allowNativeTouchScrolling: true,
        onDrag: updateProgress.value,
        onThrowUpdate: updateProgress.value,
        snap: {
          x: gsap.utils.snap(slideWidth.value),
        },
        allowContextMenu: true, // make it clickable with right click
      });

      /* REVEAL ON SCROLL */
      gsap.set([newsitemsImages, newsitems], { autoAlpha: 0 });

      gsap.timeline({
        repeat: -1,
        scrollTrigger: {
          trigger: slider.value,
          start: `top 85%`,
          once: true,

          onEnter: () => {
            // Helperfunctie om random vertragingen te genereren
            function generateRandomDelays(length, min = 0, max = 0.5) {
              const delays = Array.from(
                { length },
                () => Math.random() * (max - min) + min
              );
              return delays;
            }

            // Vertragingen genereren gebaseerd op het aantal elementen in de arrays
            const delays = generateRandomDelays(newsitems.length);

            gsap
              .timeline()
              .fromTo(
                newsitems,
                {
                  autoAlpha: 0,
                  yPercent: 75,
                },
                {
                  yPercent: 0,
                  autoAlpha: 1,
                  duration: 1.2,
                  stagger: (i) => delays[i], // Per index de gegenereerde vertraging

                  ease: "power2.inOut",
                }
              )
              .fromTo(
                newsitemsImages,
                {
                  yPercent: 75,
                  autoAlpha: 0,
                },
                {
                  yPercent: 0,
                  autoAlpha: 1,
                  duration: 1.5,
                  stagger: (i) => delays[i], // Per index de gegenereerde vertraging

                  ease: "power2.inOut",
                },
                "-0.1"
              )
              .fromTo(
                [prevControl, nextControl],
                {
                  autoAlpha: 0,
                  yPercent: 75,
                },
                {
                  autoAlpha: 1,
                  yPercent: 0,
                  duration: 0.2,
                  stagger: 0.2,
                  ease: "power2.inOut",
                },

                1
              );
          },
        },
      });
      /* REVEAL ON SCROLL */

      /* SET CONTAINER HEIGHT TO FIT CONTENT */
      setContainerHeightToFitContent();
      emitter.on("screen-size-changed", () => {
        setContainerHeightToFitContent();
      });
    });

    const setContainerHeightToFitContent = () => {
      // check every child's height of slider.value and set the height of slider.value to the highest child
      const children = slider.value.children;
      let maxHeight = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childHeight = child.offsetHeight;
        if (childHeight > maxHeight) {
          maxHeight = childHeight;
        }
      }
      slider.value.style.minHeight = `${maxHeight}px`;
    };

    const hoverOverItem = (event) => {
      // console.log("hoverOverItem", event);
    };
    const hoverOffItem = (event) => {
      // console.log("hoverOffItem", event);
    };
    const slidePrev = () => {
      animateSlides(-1);
    };
    const slideNext = () => {
      animateSlides(1);
    };

    const animateSlides = (direction) => {
      var x = gsap.utils.snap(
        slideWidth.value,
        gsap.getProperty(proxyElement.value, "x") + direction * slideWidth.value
      );

      gsap.to(proxyElement.value, {
        x: x,
        duration: 0.5,
        onUpdate: updateProgress.value,
      });
    };

    return {
      hoverOverItem,
      hoverOffItem,
      slider,
      slidePrev,
      slideNext,
      controls,
    };
  },
});
</script>

<style lang="scss" scoped>
.latestnews {
  &_container {
    overflow: hidden;
    position: relative;
    &:after,
    &:before {
      content: "";
      display: block;
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: calc($column-size * 4);

      background: linear-gradient(
        to right,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 80%
      );
    }
    &:before {
      right: auto;
      left: 0;
      background: linear-gradient(
        to left,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 1) 80%
      );
      width: calc($column-size * 1);
      z-index: 2;
    }
  }
  .newsitem {
    &_container {
      display: inline-block;
      margin-left: calc($column-size * 0.5);
      padding: 0.5em;
      position: absolute;
      top: 0;
      left: 0;
      width: 40vw;
      text-decoration: none;
      @include mq("small") {
        width: calc($column-size * 8);
        padding: 0 calc($column-size * 0.25);
        display: flex;
        flex-direction: column;
      }
      // @for $i from 1 through 50 {
      //   &:nth-child(#{$i}n) {
      //     left: calc(($column-size * 7 + 1rem) * ($i - 1));
      //   }
      // }
    }
    &_title {
      width: calc(100% - 1.5em);
      @include typo--latestnews-title;
      &__label {
        @include niveau-grotesk-regular;
        font-size: 0.85em;
      }
      &__container {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding-top: 0.5em;
      }
      &__arrow_container {
        width: 1em;
      }
    }
    &_date {
      @include typo--latestnews-date;
      margin-top: 0.5em;
      margin-bottom: -0.2em;
    }
    &_image {
      &__container {
        width: 100%;
        padding-top: 100%;
        overflow: hidden;
        position: relative;
      }
      // border: 1px solid red;

      background-size: cover;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      // position: absolute;
      // top: 0;
      // left: calc($column-size);
      // width: calc(100% - $column-size * 2);
      // height: 100%;
    }
  }
  &__controls {
    text-align: right;
    display: none;
    @include mq(small) {
      grid-column: 4/-4;

      display: flex;
      justify-content: flex-end;
      margin-bottom: 1rem;
    }
  }
  &__control {
    display: inline-block;
    margin-left: 1em;
    cursor: pointer;
  }
}
</style>
